import React, { use } from 'react';
import StatCard from './StatCard';
import { DeviceContext } from '@/hooks/common/DeviceContext';

interface DashboardStatsProps {
  pendingCount: number;
  approvedCount: number;
  totalCount: number;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

/**
 * DashboardStats - Statistics overview for admin dashboard
 * 
 * Displays key metrics in a grid of stat cards showing pending approvals,
 * approved users, and total users with appropriate icons and descriptions.
 */
const DashboardStats: React.FC<DashboardStatsProps> = ({
  pendingCount,
  approvedCount,
  totalCount,
  activeSection,
  onSectionChange
}) => {
  const { isMobile } = use(DeviceContext) ?? { isTablet: false, isMobile: false };

  const statsConfig = [
    {
      id: 'pending-approvals',
      title: 'Pending Approvals',
      value: pendingCount,
      icon: (
        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: 'bg-orange-100',
      description: 'Users waiting for approval',
      display: true,
    },
    {
      id: 'approved-users',
      title: 'Approved Users',
      value: approvedCount,
      icon: (
        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: 'bg-green-100',
      description: 'Active platform users',
      display: true,
    },
    {
      id: 'total-users',
      title: 'Total Users',
      value: totalCount,
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      bgColor: 'bg-blue-100',
      description: 'All registered users',
      display: !isMobile,
    }
  ];

  const handleCardClick = (statId: string) => {
    if (isMobile && onSectionChange) {
      const section = statId === 'pending-approvals' ? 'pending' : 'approved';
      onSectionChange(section);
    }
  };

  return (
    <div className={`grid gap-6 mb-8 ${isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
      {statsConfig.map((stat) => stat.display && (
        <StatCard
          key={stat.id}
          title={stat.title}
          value={stat.value}
          icon={stat.icon}
          bgColor={stat.bgColor}
          description={stat.description}
          isClickable={isMobile}
          isActive={isMobile && activeSection === (stat.id === 'pending-approvals' ? 'pending' : 'approved')}
          onClick={() => handleCardClick(stat.id)}
        />
      ))}
    </div>
  );
};

export default DashboardStats;
